import { KeyboardEvent, MouseEvent, useState } from 'react';

import {
  Tier,
  TIER_TO_TRACK_CODE,
  TIER_TO_CAREER_TRACK_NAME,
  TRACK_CODE_TO_NAME,
  TRACK_CODE_ORDER,
  CareerTrackRadio,
} from '@backrs_ui/common-ui';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { CareerTracksModal } from './CareerTracksModal';

const TIER_DISPLAY_ORDER: Tier[] = TRACK_CODE_ORDER.map((code) => TRACK_CODE_TO_NAME[code]);

type MinimumTrackCardProps = Readonly<{
  tier: Tier;
  /** When false, pills do not open the modal on click. Defaults to true. */
  interactive?: boolean;
  /** When true, render only the selected tier pill (used in Explore Tradeoffs view). */
  selectedOnly?: boolean;
}>;

export function MinimumTrackCard({
  tier,
  interactive = true,
  selectedOnly = false,
}: MinimumTrackCardProps) {
  const [modalTier, setModalTier] = useState<Tier | null>(null);

  const handlePillClick = (e: MouseEvent | KeyboardEvent, t: Tier) => {
    if (!interactive) return;

    e.stopPropagation();
    setModalTier(t);
  };

  const tiers = selectedOnly ? [tier] : TIER_DISPLAY_ORDER;

  return (
    <>
      <Box sx={containerSx}>
        {tiers.map((t) => {
          const isSelected = t === tier;

          return (
            <Box
              key={t}
              id={`minimum-track-pill-${TIER_TO_TRACK_CODE[t]}`}
              sx={pillSx(isSelected, interactive)}
              onClick={(e) => handlePillClick(e, t)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePillClick(e, t);
                }
              }}
              role={interactive ? 'button' : undefined}
              tabIndex={interactive ? 0 : -1}
            >
              <CareerTrackRadio careerTrack={TIER_TO_CAREER_TRACK_NAME[t]} width={16} />
              <Typography variant="text-sm-medium" sx={pillTextSx}>
                {t}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <CareerTracksModal
        open={modalTier !== null}
        onClose={() => setModalTier(null)}
        initialTier={modalTier ?? tier}
      />
    </>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const pillSx =
  (isSelected: boolean, interactive: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-sm'],
    paddingTop: backrs.spacing['spacing-xxs'],
    paddingBottom: backrs.spacing['spacing-xxs'],
    paddingLeft: backrs.spacing['spacing-xs'],
    paddingRight: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-full'],
    border: `1px solid ${backrs.colors[isSelected ? 'Brand/500' : 'Gray/900']}`,
    backgroundColor: isSelected ? backrs.colors['Brand/50'] : backrs.colors['Colors/Base/White'],
    opacity: isSelected ? 1 : 0.6,
    whiteSpace: 'nowrap',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'opacity 0.2s ease, border-color 0.2s ease',
    ...(interactive && {
      '&:hover': {
        opacity: 1,
        borderColor: backrs.colors['Brand/500'],
      },
    }),
    '& .MuiButtonBase-root.MuiRadio-root': { marginRight: 0 },
  });

const pillTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
});
